import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsShare, BsPeople, BsPerson } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useAuth } from '#globalContexts/AuthProvider';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import {
	useShareProspectQuery,
	useSharesForQuery,
	useUnshareProspectQuery,
} from '../../models/prospectQueries/sharingQueries';

export const ShareQueryModal = ({ show, onClose, query }) => {
	const { user } = useAuth();
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];
	const { data: existingShares = [] } = useSharesForQuery(query?.id);
	const shareMutation = useShareProspectQuery();
	const unshareMutation = useUnshareProspectQuery();

	const [shareWithOrg, setShareWithOrg] = useState(false);
	const [selectedMemberId, setSelectedMemberId] = useState('');
	const [feedback, setFeedback] = useState(null);

	const otherMembers = members.filter((m) => m.userId !== user?.id);
	const isSharedWithOrg = existingShares.some((s) => s.sharedWithUserId === null);

	const handleShare = async () => {
		setFeedback(null);
		try {
			if (shareWithOrg) {
				await shareMutation.mutateAsync({ queryId: query.id, shareWithOrg: true });
				setFeedback({ type: 'success', message: 'Mit der gesamten Organisation geteilt.' });
			} else if (selectedMemberId) {
				await shareMutation.mutateAsync({ queryId: query.id, sharedWithUserId: selectedMemberId });
				const member = otherMembers.find((m) => m.userId === selectedMemberId);
				setFeedback({ type: 'success', message: `Geteilt mit ${member?.email || 'Mitglied'}.` });
				setSelectedMemberId('');
			}
		} catch {
			setFeedback({ type: 'error', message: 'Fehler beim Teilen der Suchanfrage.' });
		}
	};

	const handleUnshare = async (shareId) => {
		setFeedback(null);
		try {
			await unshareMutation.mutateAsync(shareId);
			setFeedback({ type: 'success', message: 'Freigabe entfernt.' });
		} catch {
			setFeedback({ type: 'error', message: 'Fehler beim Entfernen der Freigabe.' });
		}
	};

	if (!query) return null;

	return (
		<Modal isOpen={show} onClose={onClose}>
			<Panel.Header
				icon={BsShare}
				onClose={onClose}
				subtitle={
					<>
						<strong>{query.queryName}</strong> mit Teammitgliedern teilen.
					</>
				}
			>
				Suchanfrage teilen
			</Panel.Header>
			<Panel.Body>
				<div className="space-y-3 mb-4">
					<label className="flex items-center gap-3 p-3 border border-bgBase-400 rounded-lg cursor-pointer hover:bg-bgBase-300">
						<input
							type="radio"
							name="share-target"
							checked={shareWithOrg}
							onChange={() => setShareWithOrg(true)}
							className="accent-primary-500"
						/>
						<BsPeople className="w-4 h-4 text-bgBase-800" />
						<div>
							<span className="text-sm font-medium text-text">Gesamte Organisation</span>
							<p className="text-xs text-bgBase-800">Alle Mitglieder können diese Suchanfrage sehen.</p>
						</div>
					</label>
					<label className="flex items-center gap-3 p-3 border border-bgBase-400 rounded-lg cursor-pointer hover:bg-bgBase-300">
						<input
							type="radio"
							name="share-target"
							checked={!shareWithOrg}
							onChange={() => setShareWithOrg(false)}
							className="accent-primary-500"
						/>
						<BsPerson className="w-4 h-4 text-bgBase-800" />
						<div>
							<span className="text-sm font-medium text-text">Bestimmtes Mitglied</span>
							<p className="text-xs text-bgBase-800">
								Nur die ausgewählte Person kann die Suchanfrage sehen.
							</p>
						</div>
					</label>
				</div>

				{!shareWithOrg && (
					<Select
						value={selectedMemberId}
						onChange={(e) => setSelectedMemberId(e.target.value)}
						className="mb-4"
					>
						<option value="">Mitglied auswählen...</option>
						{otherMembers.map((m) => (
							<option key={m.userId} value={m.userId}>
								{m.fullName || m.email}
							</option>
						))}
					</Select>
				)}

				<Button
					variant="solid"
					size="md"
					fullWidth
					onClick={handleShare}
					disabled={
						shareMutation.isPending ||
						(!shareWithOrg && !selectedMemberId) ||
						(shareWithOrg && isSharedWithOrg)
					}
					loading={shareMutation.isPending}
				>
					{shareWithOrg && isSharedWithOrg ? 'Bereits mit Organisation geteilt' : 'Teilen'}
				</Button>

				{feedback && (
					<p
						className={`mt-3 text-sm ${feedback.type === 'success' ? 'text-success-700' : 'text-warning-700'}`}
					>
						{feedback.message}
					</p>
				)}

				{existingShares.length > 0 && (
					<div className="mt-6 border-t pt-4">
						<h6 className="text-sm font-medium text-text mb-3">Aktive Freigaben</h6>
						<div className="space-y-2">
							{existingShares.map((share) => {
								const member = members.find((m) => m.userId === share.sharedWithUserId);
								return (
									<div
										key={share.id}
										className="flex items-center justify-between py-2 px-3 bg-bgBase-200 rounded-lg"
									>
										<span className="text-sm text-text">
											{share.sharedWithUserId === null
												? 'Gesamte Organisation'
												: member?.fullName || member?.email || share.sharedWithUserId}
										</span>
										<button
											onClick={() => handleUnshare(share.id)}
											disabled={unshareMutation.isPending}
											className="text-xs text-warning-700 hover:text-warning-700 hover:underline"
										>
											Entfernen
										</button>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</Panel.Body>
		</Modal>
	);
};

ShareQueryModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	query: PropTypes.shape({
		id: PropTypes.string.isRequired,
		queryName: PropTypes.string,
	}),
};
